/* eslint-disable camelcase */
import { datadogLogs } from '@datadog/browser-logs';
import { pickBy } from 'ramda';
import { createStore } from 'zustand/vanilla';

import { LoadTableConfigResponse } from '@ezugi/bootstrap/src/store/epics/config/fetch';
import { queryParams, getPlatform } from '@ezugi/utils';

import { ABTestingPayload, DataLayerEvent } from './types';

type AnalyticsStore = {
  datadogEnabled: boolean;
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  setDatadogEnabled: (value: boolean) => void;
  basePayload: Partial<DataLayerEvent>;
  setBasePayload: (data?: LoadTableConfigResponse['result']) => void;
  abTestingPayload?: ABTestingPayload;
  setABTestingPayload: (params: ABTestingPayload) => void;
};

export const analyticsStore = createStore<AnalyticsStore>((set) => ({
  datadogEnabled: false,
  setDatadogEnabled: (datadogEnabled: boolean) => set(() => ({ datadogEnabled })),
  enabled: false,
  setEnabled: (enabled: boolean) => set(() => ({ enabled })),
  basePayload: {
    // Have something until loadTableConfig.php responds / fails
    custom_ezugi_timestamp: Date.now(),
    operatorId: queryParams.params.operatorId,
    platform: getPlatform(),
    gameTypeId: String(queryParams.params.gameId),
    tableId: queryParams.params.tableId,
    name: import.meta.env.VITE_GAME_NAME,
  },
  setBasePayload: (data?: LoadTableConfigResponse['result']) => set(() => {
    const basePayload: Partial<DataLayerEvent> = {};

    const { gameId, operatorId, playerId, tableId } = queryParams.params;

    basePayload.operatorId = operatorId;
    basePayload.playerId = data?.userData?.uid ?? playerId;
    basePayload.gameTypeId = String(gameId);
    basePayload.custom_ezugi_timestamp = Date.now();
    basePayload.platform = getPlatform();

    const clientIdConstructedFromQs = playerId
      ? `${basePayload.operatorId}|${basePayload.playerId}|${tableId}`
      : undefined;

    basePayload.dealer_id = data?.tables?.[tableId]?.dealer_id;
    // client_id comes wrong from PHP Proxy, instead of tableId, it contains "lobby"
    basePayload.clientId = data?.userData?.client_id
      .split('|')
      .map((value, index) => (index === 2 ? tableId : value))
      .join('|')
      ?? clientIdConstructedFromQs;
    basePayload.name = import.meta.env.VITE_GAME_NAME;
    basePayload.tableId = tableId;

    if (data?.userData?.session_id) {
      basePayload.ezugi_session_id = data.userData.session_id;
    }

    return { basePayload };
  }),
  clearBasePayload: () => set(() => ({ basePayload: {} })),
  setABTestingPayload: (abTestingPayload: ABTestingPayload) => set(() => ({ abTestingPayload })),
}));

const hasOwn = (object: object, property: PropertyKey) => Object.prototype.hasOwnProperty.call(object, property);

const withTimeProperties = (eventData: DataLayerEvent) => {
  const hasCreatedAt = hasOwn(eventData, 'created_at');
  const hasLocalTime = hasOwn(eventData, 'local_time');

  if (hasCreatedAt && hasLocalTime) return eventData;

  const date = new Date();

  return {
    ...eventData,
    created_at: eventData.created_at || date.toUTCString(),
    local_time: eventData.local_time || date.toString(),
  };
};

const withABTestingPayload = () => {
  const { abTestingPayload } = analyticsStore.getState();
  const candidatePayload = {
    ui_version: abTestingPayload?.uiVersion,
    matka_auto_place_bet: abTestingPayload?.matkaAutoPlaceBet,
    matka_hide_numbers_grid_until_bet_placed: abTestingPayload?.matkaHideNumbersGridUntilBetPlaced,
  };
  return pickBy<
    typeof candidatePayload,
    Partial<typeof candidatePayload>
  >((value) => value !== undefined, candidatePayload);
};

export const sendAnalytics = (eventData: DataLayerEvent) => {
  const { basePayload } = analyticsStore.getState();
  const event: DataLayerEvent = {
    ...basePayload,
    ...withTimeProperties(eventData),
    ...withABTestingPayload(),
  };

  // NOTE: DataDog Custom logging pinched in Google Analytics logic, part of the fading out of Google Analytics.
  datadogLogs.logger.info(event.name || 'Google Analytics Event', event);

  if (window.dataLayer) {
    window.dataLayer.push(event);
  }
};
